import React, { type JSX } from "react";

import { BlockComponentProps } from "../../utils/types";

// display: contents lets each block render directly under the parent grid
// (subgrid layout relies on direct-child relationships). Trade-offs: any
// outer selector targeting the block via `> .foo` will now match this wrapper
// instead, padding/margin/position on the wrapper are ignored, and the
// element is dropped from the accessibility tree in some browsers.
const wrapperStyle: React.CSSProperties = { display: "contents" };

// Attribute stamped on the wrapper div around each top-level block. Exported
// so consumers that traverse the StreamField DOM (e.g. CirclesPromo walking
// back to a BgTransitionSentinel) can detect the wrapper without duplicating
// the literal string.
export const TRACKED_SECTION_ATTR = "data-track-section";

// Block map where id/type/index are required (TrackedStreamField always passes them).
export type StrictBlockMap<FieldValue> =
    FieldValue extends ReadonlyArray<infer Block>
        ? Block extends { id: string; type: string; value: unknown }
            ? {
                  [B in Block as B["type"]]: React.ComponentType<
                      BlockComponentProps<B["value"], B["type"]>
                  >;
              }
            : never
        : never;

type TrackedStreamFieldProps<FieldValue> =
    FieldValue extends ReadonlyArray<{
        id: string;
        type: string;
        value: unknown;
    }>
        ? {
              components: StrictBlockMap<FieldValue>;
              value: FieldValue;
              tagName?: keyof JSX.IntrinsicElements | typeof React.Fragment;
              attrs?: React.HTMLAttributes<HTMLElement>;
          }
        : never;

export const TrackedStreamField = <FieldValue = [],>(
    props: TrackedStreamFieldProps<FieldValue>,
) => {
    const tagName = props.tagName ?? "div";
    const children = props.value.map((block, i) => {
        const BlockComponent = props.components[block.type];
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!BlockComponent) return null;
        return (
            <div
                key={block.id}
                style={wrapperStyle}
                {...{ [TRACKED_SECTION_ATTR]: `${block.type}_${i}` }}
            >
                <BlockComponent
                    id={block.id}
                    type={block.type}
                    index={i}
                    value={block.value}
                />
            </div>
        );
    });
    return React.createElement(tagName, props.attrs, children);
};
